import React, { useState } from "react";
import { Share2, Copy, Twitter, Instagram, Music2 } from "lucide-react";
import { toast } from "sonner";

/**
 * One-tap campaign sharer for the Creator Awards.
 * Generates pre-filled posts for X / Instagram copy / TikTok copy
 * with the creator's vote-link + current rank.
 */
export default function ShareCampaign({ creator, rank, voteUrl }) {
  const [open, setOpen] = useState(false);
  const backend = process.env.REACT_APP_BACKEND_URL;
  // Use the OG-enriched URL when available (creator detail links are passed as voteUrl).
  // Social crawlers (X, FB, IG, LinkedIn, Discord) will fetch this and get proper preview cards.
  const shareUrl = voteUrl && voteUrl.includes("/watch/")
    ? `${backend}/api/og${voteUrl.replace(window.location.origin, "")}`
    : voteUrl;

  const rankLine = rank ? `Currently #${rank} in the Tap In TV Creator Awards — help me climb!` : "Cast your vote for me in the Tap In TV Creator Awards!";
  const text = `${rankLine}\n\nVote here → ${shareUrl}\n\n#TapInTV #CreatorAwards`;

  const copy = async (payload) => {
    try {
      await navigator.clipboard.writeText(payload);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Copy blocked — long-press to copy manually");
    }
  };

  const openX = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <button
        data-testid="share-campaign-btn"
        onClick={() => setOpen(true)}
        className="bg-tv-brand hover:bg-yellow-500 text-black font-bold px-5 py-2.5 rounded-full flex items-center gap-2"
      >
        <Share2 className="w-4 h-4" /> Share my campaign
      </button>

      {open && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div data-testid="share-campaign-modal" className="tv-glass rounded-2xl w-full max-w-lg border border-white/10 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/10">
              <div className="text-xs uppercase tracking-widest text-tv-accent font-bold">Share your campaign</div>
              <div className="font-display font-bold text-white text-xl mt-1">{creator?.name || "Your"} Campaign</div>
            </div>

            <div className="p-5 space-y-4">
              <div className="tv-glass rounded-lg border border-white/10 p-3">
                <div className="text-xs uppercase tracking-wider text-white/50 font-semibold mb-1">Pre-filled post</div>
                <pre data-testid="share-campaign-text" className="whitespace-pre-wrap text-white/90 text-sm leading-relaxed">{text}</pre>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <button
                  data-testid="share-btn-twitter"
                  onClick={openX}
                  className="flex items-center justify-center gap-2 bg-black text-white border border-white/20 hover:bg-white/5 px-3 py-2.5 rounded-lg text-sm font-semibold"
                >
                  <Twitter className="w-4 h-4" /> Post on X
                </button>
                <button
                  data-testid="share-btn-instagram"
                  onClick={() => copy(text)}
                  className="flex items-center justify-center gap-2 bg-gradient-to-tr from-pink-500 to-yellow-400 text-black hover:brightness-110 px-3 py-2.5 rounded-lg text-sm font-semibold"
                >
                  <Instagram className="w-4 h-4" /> Copy for IG
                </button>
                <button
                  data-testid="share-btn-tiktok"
                  onClick={() => copy(text)}
                  className="flex items-center justify-center gap-2 bg-white text-black hover:bg-white/90 px-3 py-2.5 rounded-lg text-sm font-semibold"
                >
                  <Music2 className="w-4 h-4" /> Copy for TikTok
                </button>
                <button
                  data-testid="share-btn-copy-link"
                  onClick={() => copy(shareUrl)}
                  className="flex items-center justify-center gap-2 tv-glass text-white border border-white/20 hover:bg-white/5 px-3 py-2.5 rounded-lg text-sm font-semibold"
                >
                  <Copy className="w-4 h-4" /> Copy link
                </button>
              </div>

              <div className="text-xs text-white/50 text-center">
                Pro tip: creators who share daily finish the season with <span className="text-tv-brand font-semibold">3× more votes</span> on average.
              </div>
            </div>
            <div className="p-4 border-t border-white/10 flex justify-end">
              <button onClick={() => setOpen(false)} className="text-white/70 px-4 py-2">Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
